import { SUPPORT_WHATSAPP_LINK } from "@/common/constants";

export const DEFAULT_WHATSAPP_COUNTRY = "CO";

export const WHATSAPP_MIN_LOCAL_DIGITS = 7;

// E.164: indicativo + numero local no pasa de 15 digitos.
export const WHATSAPP_MAX_DIGITS = 15;

const toFlagEmoji = (code) =>
    String.fromCodePoint(
        ...code.split("").map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 65)
    );

// Misma tabla pais -> indicativo que backend (contrato de
// requirements/whatsapp-indicativo-por-pais.md). Orden alfabetico por nombre.
export const WHATSAPP_COUNTRIES = [
    { code: "AR", name: "Argentina", dialCode: "54" },
    { code: "BO", name: "Bolivia", dialCode: "591" },
    { code: "CL", name: "Chile", dialCode: "56" },
    { code: "CO", name: "Colombia", dialCode: "57" },
    { code: "CR", name: "Costa Rica", dialCode: "506" },
    { code: "CU", name: "Cuba", dialCode: "53" },
    { code: "EC", name: "Ecuador", dialCode: "593" },
    { code: "SV", name: "El Salvador", dialCode: "503" },
    { code: "ES", name: "España", dialCode: "34" },
    { code: "GT", name: "Guatemala", dialCode: "502" },
    { code: "GQ", name: "Guinea Ecuatorial", dialCode: "240" },
    { code: "HN", name: "Honduras", dialCode: "504" },
    { code: "MX", name: "México", dialCode: "52" },
    { code: "NI", name: "Nicaragua", dialCode: "505" },
    { code: "PA", name: "Panamá", dialCode: "507" },
    { code: "PY", name: "Paraguay", dialCode: "595" },
    { code: "PE", name: "Perú", dialCode: "51" },
    { code: "PR", name: "Puerto Rico", dialCode: "1" },
    { code: "DO", name: "República Dominicana", dialCode: "1" },
    { code: "UY", name: "Uruguay", dialCode: "598" },
    { code: "VE", name: "Venezuela", dialCode: "58" }
].map((country) => ({ ...country, flag: toFlagEmoji(country.code) }));

const PUERTO_RICO_AREA_CODES = ["787", "939"];

const WHATSAPP_COUNTRY_REQUEST_MESSAGE =
    "Hola, quiero poner el WhatsApp de mi negocio en el portal, pero mi país no aparece en la lista. ¿Me ayudan a agregarlo? Mi país es: ";

export function sanitizeWhatsappDigits(rawValue) {
    return String(rawValue || "").replace(/\D/g, "");
}

export function normalizeWhatsappCountryCode(code) {
    const normalized = String(code || "").toUpperCase();
    return WHATSAPP_COUNTRIES.some((country) => country.code === normalized)
        ? normalized
        : DEFAULT_WHATSAPP_COUNTRY;
}

export function getWhatsappCountry(code) {
    const normalized = normalizeWhatsappCountryCode(code);
    return WHATSAPP_COUNTRIES.find((country) => country.code === normalized);
}

export function getWhatsappLocalMaxLength(countryCode) {
    return (
        WHATSAPP_MAX_DIGITS - getWhatsappCountry(countryCode).dialCode.length
    );
}

export function isValidWhatsappLocalNumber(countryCode, localNumber) {
    const value = String(localNumber || "");
    return (
        /^\d+$/.test(value) &&
        value.length >= WHATSAPP_MIN_LOCAL_DIGITS &&
        value.length <= getWhatsappLocalMaxLength(countryCode)
    );
}

// Reglas del contrato: (1) prefijo de defaultCountry si deja >= 7 digitos;
// (2) prefijo mas largo de la tabla; empate en "1" -> defaultCountry si es
// uno de los empatados, si no area 787/939 -> PR, cualquier otra -> DO.
export function splitWhatsappNumber(fullNumber, defaultCountry) {
    const digits = sanitizeWhatsappDigits(fullNumber);
    const defaultCode = normalizeWhatsappCountryCode(defaultCountry);
    if (!digits) return { country: defaultCode, localNumber: "" };

    const defaultDialCode = getWhatsappCountry(defaultCode).dialCode;
    if (
        digits.startsWith(defaultDialCode) &&
        digits.length - defaultDialCode.length >= WHATSAPP_MIN_LOCAL_DIGITS
    ) {
        return {
            country: defaultCode,
            localNumber: digits.slice(defaultDialCode.length)
        };
    }

    const matches = WHATSAPP_COUNTRIES.filter((country) =>
        digits.startsWith(country.dialCode)
    );
    if (!matches.length) return { country: defaultCode, localNumber: digits };

    const longest = Math.max(...matches.map((c) => c.dialCode.length));
    const tied = matches.filter((c) => c.dialCode.length === longest);
    const dialCode = tied[0].dialCode;
    const localNumber = digits.slice(dialCode.length);

    if (tied.length === 1) return { country: tied[0].code, localNumber };
    if (tied.some((c) => c.code === defaultCode)) {
        return { country: defaultCode, localNumber };
    }
    const areaCode = localNumber.slice(0, 3);
    return {
        country: PUERTO_RICO_AREA_CODES.includes(areaCode) ? "PR" : "DO",
        localNumber
    };
}

export function buildWhatsappNumber(countryCode, localNumber) {
    const digits = sanitizeWhatsappDigits(localNumber);
    if (!digits) return "";
    return `${getWhatsappCountry(countryCode).dialCode}${digits}`;
}

export function buildWhatsappWaMeLink(fullNumber) {
    const digits = sanitizeWhatsappDigits(fullNumber);
    return digits ? `https://wa.me/${digits}` : "";
}

export function buildWhatsappCountryRequestLink() {
    return `${SUPPORT_WHATSAPP_LINK}?text=${encodeURIComponent(
        WHATSAPP_COUNTRY_REQUEST_MESSAGE
    )}`;
}
